import { useState } from 'react'
import { AuthCard } from './AuthCard'
import { AuthInput } from './AuthInput'
import { AuthButton } from './AuthButton'
import { UserPlus, Shield, LogIn, ChevronLeft, ChevronRight } from 'lucide-react'
import { AuthState } from '@/hooks/useCustomAuth'

interface AuthFormData {
  email: string
  password: string
  confirmPassword: string
  phoneNumber?: string
  name?: string
  given_name?: string
  family_name?: string
  preferred_username?: string
  nickname?: string
  birthdate?: string
  gender?: string
  address?: string
  locale?: string
  zoneinfo?: string
  website?: string
  picture?: string
  profile?: string
  [key: string]: string | undefined
}

interface SignUpFormProps {
  onSuccess?: () => void
  authState: any
  handleSignUp: (formData: AuthFormData) => Promise<{ success: boolean; needsConfirmation?: boolean; error?: any }>
  changeState: (newState: AuthState, tempEmail?: string, fromLogin?: boolean) => void
  userPoolConfig?: any
}

export function SignUpForm({ onSuccess, authState, handleSignUp, changeState, userPoolConfig }: SignUpFormProps) {
  const [formData, setFormData] = useState<AuthFormData>({
    email: '',
    password: '',
    confirmPassword: ''
  })
  const [currentStep, setCurrentStep] = useState(1)

  // Attribute definitions for dynamic rendering
  const attributeDefinitions: Record<string, { label: string; type: string; placeholder: string; field?: string; order: number; step: number }> = {
    email: { label: 'E-mail', type: 'email', placeholder: 'seu.email@monumenta.com.br', field: 'email', order: 10, step: 1 },
    phone_number: { label: 'Telefone', type: 'tel', placeholder: '+55 11 99999-9999', field: 'phoneNumber', order: 15, step: 1 },
    preferred_username: { label: 'Nome de usuário', type: 'text', placeholder: 'Digite seu nome de usuário', order: 18, step: 1 },

    given_name: { label: 'Primeiro nome', type: 'text', placeholder: 'Digite seu primeiro nome', order: 30, step: 2 },
    family_name: { label: 'Sobrenome', type: 'text', placeholder: 'Digite seu sobrenome', order: 31, step: 2 },
    name: { label: 'Nome completo', type: 'text', placeholder: 'Digite seu nome completo', order: 32, step: 2 },
    nickname: { label: 'Apelido', type: 'text', placeholder: 'Digite seu apelido', order: 35, step: 2 },
    birthdate: { label: 'Data de nascimento', type: 'date', placeholder: '', order: 40, step: 2 },
    gender: { label: 'Gênero', type: 'text', placeholder: 'Digite seu gênero', order: 41, step: 2 },

    address: { label: 'Endereço', type: 'text', placeholder: 'Digite seu endereço completo', order: 50, step: 3 },
    locale: { label: 'Localização', type: 'text', placeholder: 'pt-BR', order: 60, step: 3 },
    zoneinfo: { label: 'Fuso horário', type: 'text', placeholder: 'America/Sao_Paulo', order: 61, step: 3 },

    website: { label: 'Website', type: 'url', placeholder: 'https://seusite.com', order: 70, step: 4 },
    picture: { label: 'Foto de perfil', type: 'url', placeholder: 'https://exemplo.com/foto.jpg', order: 71, step: 4 },
    profile: { label: 'Perfil', type: 'url', placeholder: 'https://linkedin.com/in/seuperfil', order: 72, step: 4 }
  }

  // Build list of attributes to render
  const attributesToRender: string[] = (() => {
    const attrNames: string[] = [
      ...(userPoolConfig?.requiredAttributes?.map((attr: any) => attr.name) ?? []),
      ...(userPoolConfig?.writeableAttributes ?? [])
    ]

    if (!attrNames.includes('email')) attrNames.push('email')

    const uniqueAttrs: string[] = []
    const usedFields = new Set<string>()

    for (const attr of attrNames) {
      const fieldKey = attributeDefinitions[attr]?.field ?? attr
      if (!usedFields.has(fieldKey)) {
        usedFields.add(fieldKey)
        uniqueAttrs.push(attr)
      }
    }

    return uniqueAttrs.sort((a, b) =>
      (attributeDefinitions[a]?.order ?? 1000) - (attributeDefinitions[b]?.order ?? 1000)
    )
  })()

  // Stepper configuration
  const STEP_THRESHOLD = 5
  const needsStepper = attributesToRender.length > STEP_THRESHOLD

  // Step definitions
  const stepConfig = [
    { number: 1, title: 'Conta', description: 'Informações básicas de acesso' },
    { number: 2, title: 'Identidade', description: 'Dados pessoais' },
    { number: 3, title: 'Contato', description: 'Informações de contato' },
    { number: 4, title: 'Perfil', description: 'Informações do perfil' }
  ]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Password validation
    if (formData.password !== formData.confirmPassword) {
      return
    }

    try {
      const result = await handleSignUp(formData)

      if (result?.success) {
        onSuccess?.()
      }
    } catch (error) {
      console.error('Sign up error:', error)
    }
  }

  const updateFormData = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const getFieldsForStep = (step: number) => {
    return attributesToRender.filter(attr =>
      attributeDefinitions[attr]?.step === step
    )
  }

  const isStepValid = (step: number) => {
    const fields = getFieldsForStep(step)

    if (step === 1) {
      // Always require email and password
      if (!formData.email || !formData.password || !formData.confirmPassword) return false
      if (formData.password !== formData.confirmPassword) return false
    }

    // Check required fields for this step
    const requiredAttrs = userPoolConfig?.requiredAttributes?.map((attr: any) => attr.name) ?? []
    for (const attr of fields) {
      if (requiredAttrs.includes(attr)) {
        const fieldKey = attributeDefinitions[attr]?.field ?? attr
        if (!formData[fieldKey]) return false
      }
    }

    return true
  }

  const canProceed = () => {
    if (needsStepper) {
      return isStepValid(currentStep)
    } else {
      return formData.email && formData.password && formData.confirmPassword &&
             formData.password === formData.confirmPassword
    }
  }

  const renderStepContent = () => {
    if (!needsStepper) {
      return renderAllFields()
    }

    const fields = getFieldsForStep(currentStep)

    return (
      <div className="space-y-6">
        {currentStep === 1 && (
          <>
            <AuthInput
              label="E-mail"
              type="email"
              value={formData.email}
              onChange={(value) => updateFormData('email', value)}
              placeholder="seu.email@monumenta.com.br"
              required
              data-testid="signup-email-input"
            />
            <AuthInput
              label="Senha"
              type="password"
              value={formData.password}
              onChange={(value) => updateFormData('password', value)}
              placeholder="Digite uma senha segura"
              required
              data-testid="signup-password-input"
            />
            <AuthInput
              label="Confirmar senha"
              type="password"
              value={formData.confirmPassword}
              onChange={(value) => updateFormData('confirmPassword', value)}
              placeholder="Digite a senha novamente"
              required
              error={formData.confirmPassword && formData.password !== formData.confirmPassword ? 'As senhas não coincidem' : undefined}
              data-testid="signup-confirm-password-input"
            />
          </>
        )}

        {fields.map(attr => {
          const def = attributeDefinitions[attr]
          if (!def || attr === 'email') return null

          const fieldKey = def.field ?? attr
          const isRequired = userPoolConfig?.requiredAttributes?.some((reqAttr: any) => reqAttr.name === attr)

          return (
            <AuthInput
              key={attr}
              label={def.label}
              type={def.type}
              value={formData[fieldKey] || ''}
              onChange={(value) => updateFormData(fieldKey, value)}
              placeholder={def.placeholder}
              required={isRequired}
              data-testid={`signup-${fieldKey}-input`}
            />
          )
        })}
      </div>
    )
  }

  const renderAllFields = () => {
    return (
      <div className="space-y-6">
        <AuthInput
          label="E-mail"
          type="email"
          value={formData.email}
          onChange={(value) => updateFormData('email', value)}
          placeholder="seu.email@monumenta.com.br"
          required
          data-testid="signup-email-input"
        />
        <AuthInput
          label="Senha"
          type="password"
          value={formData.password}
          onChange={(value) => updateFormData('password', value)}
          placeholder="Digite uma senha segura"
          required
          data-testid="signup-password-input"
        />
        <AuthInput
          label="Confirmar senha"
          type="password"
          value={formData.confirmPassword}
          onChange={(value) => updateFormData('confirmPassword', value)}
          placeholder="Digite a senha novamente"
          required
          error={formData.confirmPassword && formData.password !== formData.confirmPassword ? 'As senhas não coincidem' : undefined}
          data-testid="signup-confirm-password-input"
        />

        {attributesToRender.filter(attr => attr !== 'email').map(attr => {
          const def = attributeDefinitions[attr]
          if (!def) return null

          const fieldKey = def.field ?? attr
          const isRequired = userPoolConfig?.requiredAttributes?.some((reqAttr: any) => reqAttr.name === attr)

          return (
            <AuthInput
              key={attr}
              label={def.label}
              type={def.type}
              value={formData[fieldKey] || ''}
              onChange={(value) => updateFormData(fieldKey, value)}
              placeholder={def.placeholder}
              required={isRequired}
              data-testid={`signup-${fieldKey}-input`}
            />
          )
        })}
      </div>
    )
  }

  const getTitle = () => {
    if (needsStepper) {
      const step = stepConfig.find(s => s.number === currentStep)
      return `${step?.title} (${currentStep}/${stepConfig.length})`
    }
    return 'Criar nova conta'
  }

  const getSubtitle = () => {
    if (needsStepper) {
      const step = stepConfig.find(s => s.number === currentStep)
      return step?.description
    }
    return 'Preencha os dados para criar sua conta no MonuTrack'
  }

  return (
    <AuthCard
      title={getTitle()}
      subtitle={getSubtitle()}
      icon={<UserPlus className="h-8 w-8 text-brand-dark" />}
    >
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="signup-form">
        {renderStepContent()}

        {authState.error && (
          <div
            className="bg-error/20 border border-error text-error px-4 py-3 rounded-monumenta text-sm"
            data-testid="signup-error-message"
          >
            {authState.error.message}
          </div>
        )}

        <div className="space-y-3">
          {needsStepper ? (
            <div className="flex gap-3">
              {currentStep > 1 && (
                <AuthButton
                  variant="secondary"
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="flex-1"
                  icon={<ChevronLeft className="h-4 w-4" />}
                  data-testid="signup-previous-button"
                >
                  Voltar
                </AuthButton>
              )}

              {currentStep < stepConfig.length ? (
                <AuthButton
                  variant="primary"
                  onClick={() => setCurrentStep(currentStep + 1)}
                  disabled={!canProceed()}
                  className="flex-1"
                  icon={<ChevronRight className="h-4 w-4" />}
                  data-testid="signup-next-button"
                >
                  Próximo
                </AuthButton>
              ) : (
                <AuthButton
                  type="submit"
                  variant="primary"
                  loading={authState.isLoading}
                  disabled={!canProceed()}
                  className="flex-1"
                  icon={<UserPlus className="h-5 w-5" />}
                  data-testid="signup-submit-button"
                >
                  Criar conta
                </AuthButton>
              )}
            </div>
          ) : (
            <AuthButton
              type="submit"
              variant="primary"
              loading={authState.isLoading}
              disabled={!canProceed()}
              className="w-full"
              icon={<UserPlus className="h-5 w-5" />}
              data-testid="signup-submit-button"
            >
              Criar conta
            </AuthButton>
          )}

          <div className="text-center" data-testid="signup-signin-section">
            <p className="text-sm text-brand-gray mb-3 font-body" data-testid="signup-signin-prompt">
              Já tem uma conta?
            </p>
            <AuthButton
              variant="ghost"
              onClick={() => changeState('signIn')}
              className="w-full"
              icon={<LogIn className="h-4 w-4" />}
              data-testid="signup-signin-button"
            >
              Fazer login
            </AuthButton>
          </div>
        </div>
      </form>
    </AuthCard>
  )
}